import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flame, CheckCircle2, ArrowRight, CalendarCheck } from "lucide-react";

export default function Completion() {
  const { day } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state || {};
  const dayNum = Number(day);

  useEffect(() => {
    if (!location.state) {
      // no state (e.g. refresh), send home
      const t = setTimeout(() => {}, 0);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  const completed = data.completed_count ?? dayNum;
  const streak = data.streak ?? 0;
  const next = data.next_lesson;
  const weekEnd = { 7: 1, 14: 2, 21: 3, 30: 4 }[dayNum] || 0;

  return (
    <div className="min-h-screen bg-brand flex items-center justify-center px-6" data-testid="completion-page">
      <div className="max-w-md w-full text-center animate-soft-scale">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto bg-white/10">
          <CheckCircle2 size={40} style={{ color: "var(--amber)" }} />
        </div>
        <p className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase mt-6">Day {dayNum} complete</p>
        <h1 className="font-serif text-4xl font-bold text-white mt-2">🎉 Day {dayNum} complete</h1>
        <p className="text-white/75 mt-4 max-w-sm mx-auto">You're building your own menopause support plan.</p>

        <div className="mt-8 grid grid-cols-2 gap-3">
          <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div className="font-serif text-3xl font-bold text-white">{completed}<span className="text-lg text-white/50">/30</span></div>
            <div className="text-white/60 text-xs mt-1">days complete</div>
          </div>
          <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div className="font-serif text-3xl font-bold text-white flex items-center justify-center gap-1.5">
              <Flame size={22} style={{ color: "var(--amber)" }} /> {streak}
            </div>
            <div className="text-white/60 text-xs mt-1">day streak</div>
          </div>
        </div>

        {next ? (
          <div className="mt-6 rounded-2xl p-5 text-left" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div className="text-white/50 text-xs font-semibold uppercase tracking-wide">Tomorrow</div>
            <div className="text-white font-semibold mt-1">Day {next.day}, {next.title}</div>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div className="text-white font-semibold">🎉 You've completed all 30 days. Beautiful work.</div>
          </div>
        )}

        <button
          data-testid="completion-done-btn"
          onClick={() => navigate("/home")}
          className="mt-8 w-full bg-white text-brand hover:bg-[var(--sage)] font-semibold py-4 rounded-full flex items-center justify-center gap-2 transition-colors"
        >
          See Tomorrow's Focus <ArrowRight size={17} />
        </button>

        {weekEnd && (
          <button
            data-testid="completion-week-summary-btn"
            onClick={() => navigate(`/summary/${weekEnd}`)}
            className="mt-3 w-full border border-white/30 text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors hover:bg-white/10"
          >
            <CalendarCheck size={17} /> See your Week {weekEnd} summary
          </button>
        )}

        {dayNum >= 30 && (
          <p className="text-white/60 text-sm mt-5 max-w-sm mx-auto">
            Your full Personal Menopause Support Plan is now ready, built from everything you entered.
          </p>
        )}
      </div>
    </div>
  );
}
